/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import { doGraphqlRequest } from 'ducks/requests/operations'
import { DEFAULT_MODULES } from 'ducks/requests/modules'
import debug from 'util/debug'
import { MAILBOX_CHANNEL_TYPE } from 'ducks/folders/constants'
import { buildId } from 'util/globalId'
import { camelize } from 'util/strings'
import Bugsnag from '@bugsnag/js'
import { selectSearchConversationsRequest } from 'ducks/searches/selectors'
import { sleep } from 'util/functions'
import { FETCH_CONVERSATION } from '../actionTypes'
import { conversationFullGraphQlResponseSchema } from '../schema'
import {
  buildConversationRequestKey,
  isValidTicketId,
  isTicketDeletedError,
  isTicketMergeError,
  isTicketNoAccess,
  isTicketNotFoundError,
} from '../utils/request'
import { ticketFullConversationQuery } from '../queries'

import { mergeTicketEntities } from '../utils/entities'

// Helper to create a normalized action without making an HTTP request
// IMPORTANT: This is tightly coupled to DEFAULT_MODULES from ducks/requests/modules.
// It manually invokes the same normalization pipeline that doGraphqlRequest would call,
// allowing us to normalize entities without making an HTTP request. If DEFAULT_MODULES
// or the module calling convention (phase, action, payload, options) changes, this could
// break silently. Consider this technical debt that should be refactored if the request
// module architecture changes.
function createNormalizedAction(payload, options, getState) {
  const action = {}
  DEFAULT_MODULES.forEach(moduleFn =>
    moduleFn('SUCCESS', action, payload, {
      ...options,
      requestType: FETCH_CONVERSATION,
      getState,
    })
  )
  return action
}

const transformAttachEventGroupIdToEvents = data => {
  if (data?.eventGroups) {
    data.eventGroups.edges.forEach((eg, index) => {
      eg.node.isFirstMessage = index === 0
      eg.node.isLastMessage = index === data.eventGroups.edges.length - 1
      eg.node.events.edges.forEach(e => {
        e.node.eventGroupId = eg.node.id

        const change = e.node.change
        if (
          change &&
          change.__typename === 'IntegrationAdded' &&
          !change.conversationId
        ) {
          change.conversationId = eg.node.conversationId
        }
      })
    })
  }
  return data
}

/*
  This method will be used to retrieve the conversations
  Currently there is a massive disparity in the api implementation between tickets and
  rooms. This method will hide those differences and return a standadised interface for
  the folder component.
 */
export function doFetchTicket({
  conversationId: inputConversationId,
  channelType,
  options = {},
}) {
  return async (dispatch, getState) => {
    if (!isValidTicketId(inputConversationId)) return null
    const conversationId = buildId('Conversation', inputConversationId)
    if (channelType !== MAILBOX_CHANNEL_TYPE) {
      debug(
        `doFetchTicket currently only supports loading the ${MAILBOX_CHANNEL_TYPE} channel type`
      )
    }
    let searchRequest = selectSearchConversationsRequest(getState())
    let tryCount = 0
    while (searchRequest.loading && tryCount < 10) {
      debug('doFetchTicket wait while search loads', {
        tryCount,
        inputConversationId,
        channelType,
        options,
      })
      // eslint-disable-next-line no-await-in-loop
      await sleep(1000)
      searchRequest = selectSearchConversationsRequest(getState())
      tryCount += 1
    }

    let attempt = 0

    const PAGE_SIZE = 10 // Number of event groups to fetch per pagination request
    const MAX_PAGINATION_ITERATIONS = 100 // Safety limit to prevent infinite loops

    while (attempt < 3) {
      attempt += 1
      try {
        let hasPreviousPage = true
        let before = null
        let previousCursor = null
        let allEventGroups = []
        let conversationNode = null
        let linkedResources = null
        let iterationCount = 0
        const seenEventGroupIds = new Set() // O(1) deduplication tracking

        // Paginate backwards through event groups, starting from most recent
        while (hasPreviousPage && iterationCount < MAX_PAGINATION_ITERATIONS) {
          iterationCount += 1
          const variables = {
            conversationId,
            eventGroupsFilter: {
              conversationId,
            },
            linkedResourcesFilter: {
              conversationId,
            },
            last: PAGE_SIZE,
            before, // null on first iteration (fetch from end)
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await dispatch(
            doGraphqlRequest(
              FETCH_CONVERSATION,
              ticketFullConversationQuery(),
              variables,
              {
                app: true,
                includeLegacyPayload: true, // Get raw response data
                throwOnError: true,
              }
            )
          )

          // Extract raw GraphQL response from the action result
          const result = response.payload || response

          // Null-safety: protect against malformed responses
          // Throw to trigger retry rather than returning partial data
          if (!result || typeof result !== 'object') {
            throw new Error(
              `Malformed GraphQL response: result is not an object for conversation ${inputConversationId}`
            )
          }

          // Check for timeout errors in the GraphQL response
          // If timeouts are detected, break pagination immediately to avoid
          // timing out 100+ times. Return the data collected so far.
          if (result.errors && Array.isArray(result.errors)) {
            const hasTimeoutError = result.errors.some(
              error =>
                error.message &&
                typeof error.message === 'string' &&
                error.message.toLowerCase().includes('timeout')
            )

            if (hasTimeoutError) {
              // Break pagination loop immediately
              break
            }
          }

          const {
            eventGroups,
            node,
            linkedResources: pageLinkedResources,
          } = result

          // Null-safety: verify eventGroups structure
          // Throw to trigger retry rather than returning partial data
          if (!eventGroups || !Array.isArray(eventGroups.edges)) {
            throw new Error(
              `Malformed GraphQL response: invalid eventGroups shape for conversation ${inputConversationId}`
            )
          }

          // Deduplicate event groups by ID using persistent Set (O(1) per page)
          const newEventGroups = eventGroups.edges.filter(edge => {
            const id = edge.node?.id
            if (!id || seenEventGroupIds.has(id)) {
              return false
            }
            seenEventGroupIds.add(id)
            return true
          })

          // Prepend older messages to the beginning of the array
          allEventGroups = [...newEventGroups, ...allEventGroups]

          // Store node and linked resources (same on all pages)
          conversationNode = node || conversationNode
          linkedResources = pageLinkedResources || linkedResources

          // Determine if we should continue pagination based on pageInfo
          // Null-safety: verify pageInfo exists
          // Throw to trigger retry rather than returning partial data
          if (
            !eventGroups.pageInfo ||
            typeof eventGroups.pageInfo !== 'object'
          ) {
            throw new Error(
              `Malformed GraphQL response: missing or invalid pageInfo for conversation ${inputConversationId}`
            )
          }

          const nextCursor = eventGroups.pageInfo.startCursor
          hasPreviousPage = eventGroups.pageInfo.hasPreviousPage || false

          // Anti-loop guard: break if server returns the same cursor
          // This prevents infinite loops if backend has a bug
          if (hasPreviousPage && nextCursor === previousCursor) {
            debug('Pagination loop detected: same cursor returned', {
              cursor: nextCursor,
              conversationId: inputConversationId,
            })
            break
          }

          previousCursor = nextCursor
          before = nextCursor
        }

        // Check if we hit the max iteration limit
        if (iterationCount >= MAX_PAGINATION_ITERATIONS) {
          const errorMsg = `Pagination hit max iterations limit (${MAX_PAGINATION_ITERATIONS})`
          debug(errorMsg, {
            conversationId: inputConversationId,
            iterationCount,
            eventGroupsLoaded: allEventGroups.length,
            hasPreviousPage,
          })
          Bugsnag.notify(new Error(errorMsg), event => {
            event.severity = 'warning'
            event.addMetadata('pagination', {
              conversationId: inputConversationId,
              iterationCount,
              eventGroupsLoaded: allEventGroups.length,
              hasPreviousPage,
              lastCursor: before,
            })
          })
        }

        // Validate pagination results before building completeData
        // conversationNode is required - throw to trigger retry if missing
        if (!conversationNode) {
          throw new Error(
            `Missing conversation node after pagination for conversation ${inputConversationId}`
          )
        }

        // Ensure linkedResources has valid structure - default to empty edges array
        // This prevents null/undefined from breaking downstream processing
        if (!linkedResources || !linkedResources.edges) {
          linkedResources = { edges: [] }
        }

        // Reconstruct the complete result with all paginated event groups
        const completeData = {
          node: conversationNode,
          eventGroups: {
            edges: allEventGroups,
          },
          linkedResources,
        }

        // Apply the transformation to the complete dataset
        const transformedData = transformAttachEventGroupIdToEvents(
          completeData
        )

        // Manually normalize and dispatch without additional HTTP request
        // This runs data through the full Redux pipeline (normalization, entity
        // merging, searches, etc.) without making an extra network call.
        const normalizedAction = createNormalizedAction(
          transformedData,
          {
            ...options,
            app: true,
            normalizationSchema: conversationFullGraphQlResponseSchema,
            transformEntities: mergeTicketEntities(getState),
            meta: {
              channelType,
              requestKey: camelize(
                buildConversationRequestKey(inputConversationId)
              ),
              mergeEntities: true,
              updateSearches: !!options.updateSearches,
            },
          },
          getState
        )

        return dispatch(normalizedAction)
      } catch (error) {
        const noAccessError = isTicketNoAccess(error)
        const deletedError = isTicketDeletedError(error)
        const mergeError = isTicketMergeError(error)
        const notFoundError = isTicketNotFoundError(error)

        const isHandled =
          noAccessError || deletedError || mergeError || notFoundError

        if (isHandled || attempt === 3) {
          if (!isHandled) {
            Bugsnag.notify(
              new Error('doFetchTicket failed to load after 3 attempts', {
                cause: error,
              }),
              // eslint-disable-next-line no-loop-func
              event => {
                // Set the severity level
                event.severity = 'error'

                // Attach additional metadata, including the original error
                event.addMetadata('metaData', {
                  ticketId: inputConversationId,
                  channelType,
                  originalError: {
                    message: error.message,
                    stack: error.stack,
                    name: error.name,
                  },
                })
              }
            )
          }

          // Dispatch FAILED action so Redux state is updated with the error
          dispatch({
            type: `${FETCH_CONVERSATION}_FAILED`,
            payload: error,
            meta: {
              channelType,
              requestKey: camelize(
                buildConversationRequestKey(inputConversationId)
              ),
            },
          })

          throw error
        }

        debug('doFetchTicket failed, retrying', {
          error,
          attempt,
        })
      }
    }
    return null
  }
}
